import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Eye, Users, MousePointerClick, TrendingUp, Phone, Mail, BedDouble, Bath, Ruler, MapPin, Globe, Facebook, Instagram, Search, Home as HomeIcon } from "lucide-react";

const NAVY = "#0D1B2A";
const ORANGE = "#E8792B";
const STAGE = { coming_soon: ["Coming Soon", "#1F3C88"], for_sale: ["For Sale", ORANGE], under_contract: ["Under Contract", "#B07D14"], sold: ["Sold", "#1F8A4C"] };
const fmtPrice = (n) => (n ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n) : "Price upon request");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const abs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const SRC_ICON = { Facebook, Instagram, Google: Search, Zillow: HomeIcon, Direct: Globe, "X (Twitter)": Globe, Email: Mail };
const DEVICE_COLORS = { Desktop: NAVY, Mobile: ORANGE, Tablet: "#9CA3AF" };

export default function AnalyticsReport() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = "Performance Report";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lm/public/report/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="min-h-screen grid place-items-center bg-[#FAFAF9] text-stone-400" style={{ fontFamily: "Manrope, sans-serif" }}>Report not available.</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-[#FAFAF9] text-stone-400">Loading…</div>;

  const p = data.property || {};
  const [stageLabel, stageColor] = STAGE[p.listing_stage] || STAGE.for_sale;
  const days = Math.max(1, data.series.length);
  const avg = Math.round((data.totals.views / days) * 10) / 10;
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const tiles = [
    { key: "views", label: "Total Visits", value: data.totals.views, Icon: Eye, tid: "report-total-views" },
    { key: "unique", label: "Unique Visitors", value: data.totals.unique, Icon: Users, tid: "report-unique" },
    { key: "leads", label: "Leads Captured", value: data.totals.leads, Icon: MousePointerClick, tid: "report-leads" },
    { key: "avg", label: "Avg Visits / Day", value: avg, Icon: TrendingUp, tid: "report-avg" },
  ];

  return (
    <div data-testid="analytics-report" className="min-h-screen bg-[#FAFAF9] text-stone-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Brand bar */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md grid place-items-center text-white font-bold text-sm" style={{ background: NAVY }}>MC</div>
            <div>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>{data.team_name}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-stone-400">Performance Report</div>
            </div>
          </div>
          <div className="text-xs text-stone-400">Prepared {today}</div>
        </div>
      </header>

      {/* Property hero */}
      <section className="relative h-[360px] w-full overflow-hidden">
        {p.hero_photo ? <img src={abs(p.hero_photo)} alt={p.address_line} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0" style={{ background: NAVY }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/25" />
        <div className="absolute inset-x-0 bottom-0">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-5xl mx-auto px-6 pb-8 text-white">
            <span data-testid="report-stage-badge" className="inline-block text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full text-white mb-3" style={{ background: stageColor }}>{stageLabel}</span>
            <h1 className="text-4xl sm:text-5xl tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{p.address_line}</h1>
            <p className="text-white/80 mt-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> {[p.city, `${p.state || ""} ${p.zip_code || ""}`.trim()].filter(Boolean).join(", ")}</p>
            <div className="mt-4 flex flex-wrap items-center gap-x-7 gap-y-2">
              <div className="text-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{fmtPrice(p.price)}</div>
              <div className="flex items-center gap-5 text-sm text-white/90">
                {p.beds != null && <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> {p.beds} Beds</span>}
                {p.baths != null && <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" /> {p.baths} Baths</span>}
                {p.sqft != null && <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> {p.sqft?.toLocaleString()} Sq Ft</span>}
              </div>
            </div>
            <div className="text-xs text-white/60 mt-3">Reporting period · {fmtDate(data.first_seen)} – {fmtDate(data.last_seen)}</div>
          </motion.div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map((t, i) => (
            <motion.div key={t.key} data-testid={t.tid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className="bg-white border border-stone-200 rounded-xl p-5">
              <t.Icon className="w-5 h-5 mb-3" style={{ color: ORANGE }} />
              <div className="text-3xl font-semibold" style={{ color: NAVY }}>{t.value?.toLocaleString?.() ?? t.value}</div>
              <div className="text-xs uppercase tracking-[0.12em] text-stone-400 mt-1">{t.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Total traffic chart */}
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <h2 className="text-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Total Traffic</h2>
          <p className="text-sm text-stone-400 mb-5">Cumulative since {fmtDate(data.first_seen)}</p>
          <div data-testid="traffic-chart" style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={data.series} margin={{ top: 10, right: 10, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ORANGE} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={ORANGE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip formatter={(v) => [`${v} total visits`, ""]} labelStyle={{ color: NAVY }} contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
                <Area type="monotone" dataKey="total" stroke={ORANGE} strokeWidth={2.5} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Sources */}
          <div className="bg-white border border-stone-200 rounded-xl p-6">
            <h2 className="text-2xl mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Where Traffic Came From</h2>
            <div className="space-y-4">
              {data.sources.map((s, i) => {
                const Icon = SRC_ICON[s.name] || Globe;
                return (
                  <div key={s.name} data-testid={`report-source-${s.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="flex items-center gap-2 text-stone-700"><Icon className="w-4 h-4 text-stone-400" /> {s.name}</span>
                      <span className="text-stone-400">{s.views} · {s.pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${s.pct}%` }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }} className="h-full rounded-full" style={{ background: NAVY }} />
                    </div>
                  </div>
                );
              })}
              {!data.sources.length && <p className="text-sm text-stone-400">No traffic yet.</p>}
            </div>
          </div>

          {/* Devices */}
          <div className="bg-white border border-stone-200 rounded-xl p-6">
            <h2 className="text-2xl mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Devices</h2>
            <div className="flex items-center gap-6">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.devices} dataKey="views" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {data.devices.map((d) => <Cell key={d.name} fill={DEVICE_COLORS[d.name] || "#9CA3AF"} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} visits`, n]} contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {data.devices.map((d) => (
                  <div key={d.name} className="flex items-center gap-2.5 text-sm">
                    <span className="w-3 h-3 rounded-sm" style={{ background: DEVICE_COLORS[d.name] || "#9CA3AF" }} />
                    <span className="text-stone-700 w-16">{d.name}</span>
                    <span className="text-stone-400">{d.pct}%</span>
                  </div>
                ))}
                {!data.devices.length && <p className="text-sm text-stone-400">No data yet.</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Agent card */}
        {data.agent && (
          <div data-testid="report-agent-card" className="rounded-xl p-6 text-white flex items-center gap-5" style={{ background: NAVY }}>
            {data.agent.headshot
              ? <img src={abs(data.agent.headshot)} alt={data.agent.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/20" />
              : <div className="w-16 h-16 rounded-full grid place-items-center bg-white/10 text-lg font-semibold">{(data.agent.name || "?")[0]}</div>}
            <div className="flex-1">
              <div className="text-lg font-semibold">{data.agent.name}</div>
              <div className="text-sm text-white/60">{[data.agent.title, data.agent.company].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="flex items-center gap-3">
              {data.agent.phone && <a href={`tel:${data.agent.phone}`} className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full transition-colors"><Phone className="w-4 h-4" /> Call</a>}
              {data.agent.email && <a href={`mailto:${data.agent.email}`} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full transition-colors" style={{ background: ORANGE }}><Mail className="w-4 h-4" /> Email</a>}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-stone-200 py-8">
        <div className="max-w-5xl mx-auto px-6 flex items-center gap-3 text-stone-400 text-sm">
          <div className="w-7 h-7 rounded grid place-items-center text-white text-xs font-bold" style={{ background: NAVY }}>MC</div>
          Generated by {data.team_name} · {today}
        </div>
      </footer>
    </div>
  );
}
